import { useTranslation } from 'react-i18next';
import {
  SubformComponent,
  SubCardFieldsComponent,
  SubModule,
} from '@/modules/admin/audits/components/submodules/types';
import { LineText } from '@common/components/fragments/LineText';
import AuditsExportsEmployeesInOutExport from '@/app/spraypaint/audits/exports/AuditsExportsEmployeesInOutExport';
import { ControlledDateInput } from '@common/components/inputs/controlledInput/ControlledDateInput';
import useLegalEntitiesSelectOptions from '@common/legalEntity/useLegalEntitiesSelectOptions';
import { ControlledSelect } from '@common/components/inputs/controlledInput/ControlledSelect';
import { usePayslipPeriodOptions } from '@/app/hooks/usePayslipPeriodOptions';
import dayjs from 'dayjs';
import useLegalEntities from '@/modules/common/legalEntity/useLegalEntities';
import { getDateFromPeriod } from '@/app/utils/dateUtils';

export const jsonApiType = AuditsExportsEmployeesInOutExport.jsonapiType;

export type Values = {
  firstPayslipPeriod: string;
  lastPayslipPeriod: string;
  legalEntityId?: AuditsExportsEmployeesInOutExport['legalEntityId'];
};

export const defaultValues: Values = {
  // Today, iso8601 format date
  firstPayslipPeriod: dayjs()
    .startOf('month')
    .subtract(1, 'month')
    .endOf('month')
    .format('YYYY-MM-DD'),
  lastPayslipPeriod: dayjs()
    .startOf('month')
    .subtract(1, 'month')
    .endOf('month')
    .format('YYYY-MM-DD'),
  legalEntityId: null,
};

function createExportStrategy(data: Values): AuditsExportsEmployeesInOutExport {
  return new AuditsExportsEmployeesInOutExport({
    firstPayslipPeriod: data.firstPayslipPeriod,
    lastPayslipPeriod: data.lastPayslipPeriod,
    legalEntityId: data.legalEntityId,
  });
}

export const FormComponent: SubformComponent = ({ control }) => {
  const { t } = useTranslation();

  const legalEntityOptions = [
    { label: t('views.legal_entity.all'), value: null },
    ...useLegalEntitiesSelectOptions(),
  ];

  const payslipPeriodOptions = usePayslipPeriodOptions();

  return (
    <>
      <ControlledSelect
        label={t(
          'activerecord.attributes.audits/exports/employees_in_out_export.first_payslip_period',
          {
            ns: 'rails',
          },
        )}
        className="mt-20"
        control={control}
        defaultValue={undefined}
        name="exportStrategy.params.firstPayslipPeriod"
        options={payslipPeriodOptions}
        required
      />
      <ControlledSelect
        label={t(
          'activerecord.attributes.audits/exports/employees_in_out_export.last_payslip_period',
          {
            ns: 'rails',
          },
        )}
        className="mt-20"
        control={control}
        defaultValue={undefined}
        name="exportStrategy.params.lastPayslipPeriod"
        options={payslipPeriodOptions}
        required
      />
      <ControlledSelect
        label={t('views.legal_entity.label')}
        className="mt-20"
        control={control}
        defaultValue={undefined}
        name="exportStrategy.params.legalEntityId"
        options={legalEntityOptions}
        placeholder="Toutes"
      />
    </>
  );
};

export const CardFieldsComponent: SubCardFieldsComponent<
  AuditsExportsEmployeesInOutExport
> = ({ strategy }) => {
  const { t } = useTranslation();
  const { legalEntities } = useLegalEntities();

  return (
    <>
      <LineText
        label={t('activerecord.attributes.audits/export.export_strategy', {
          ns: 'rails',
        })}
        value={t(
          'activerecord.models.audits/exports/employees_in_out_exports',
          {
            ns: 'rails',
          },
        )}
      />
      <LineText
        label={t(
          'activerecord.attributes.audits/exports/employees_in_out_export.first_payslip_period',
          {
            ns: 'rails',
          },
        )}
        value={getDateFromPeriod(strategy.firstPayslipPeriod)}
      />
      <LineText
        label={t(
          'activerecord.attributes.audits/exports/employees_in_out_export.last_payslip_period',
          {
            ns: 'rails',
          },
        )}
        value={getDateFromPeriod(strategy.lastPayslipPeriod)}
      />
      <LineText
        label={t(
          'activerecord.attributes.audits/exports/employees_in_out_export.legal_entity_id',
          {
            ns: 'rails',
          },
        )}
        value={
          legalEntities.find(
            (le) => String(le.id) === String(strategy.legalEntityId),
          )?.name || 'Toutes'
        }
      />
    </>
  );
};

export const AuditsExportsEmployeesInOutExportSubmodule: SubModule<
  Values,
  AuditsExportsEmployeesInOutExport
> = {
  jsonApiType,
  defaultValues,
  FormComponent,
  CardFieldsComponent,
  createExportStrategy,
};
